import { useCallback, useSyncExternalStore } from 'react'

/**
 * Breite der Beitragsansicht (Alex 2026-10-02):
 *   wide     — der Inhalt nutzt die volle Seitenbreite (bisheriges Verhalten)
 *   centered — eine gut lesbare Spalte, mittig
 *   paper    — ein Blatt im Hochformat, mittig, durchgehend weiß (Alex
 *              2026-10-03: kein abgesetztes Blatt, ein Canvas); nach unten
 *              durchlaufend ohne Seitenwechsel, nur die Breite ist begrenzt
 *
 * Die Wahl ist eine Vorliebe der Person am Gerät und liegt darum im
 * localStorage, nicht am Beitrag. Ein kleiner Speicher im Modul sorgt dafür,
 * dass alle Stellen derselben Seite (Kopfleiste, Lesemodus, Collab-Ansicht)
 * dieselbe Wahl sehen; andere Tabs folgen über das storage-Ereignis.
 */
export type LayoutMode = 'wide' | 'centered' | 'paper'

const KEY = 'content_layout'
const MODES: LayoutMode[] = ['wide', 'centered', 'paper']
const listeners = new Set<() => void>()
let current: LayoutMode = read()

function read(): LayoutMode {
  try {
    const v = localStorage.getItem(KEY)
    return MODES.includes(v as LayoutMode) ? (v as LayoutMode) : 'wide'
  } catch {
    return 'wide'
  }
}

function emit() {
  listeners.forEach((l) => l())
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== KEY) return
    current = read()
    emit()
  })
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function setLayoutMode(next: LayoutMode) {
  current = next
  try {
    localStorage.setItem(KEY, next)
  } catch {
    // Privater Modus ohne Speicher: die Wahl gilt dann nur für diese Seite.
  }
  emit()
}

export function useLayoutMode(): [LayoutMode, (mode: LayoutMode) => void] {
  const mode = useSyncExternalStore(subscribe, () => current, () => 'wide' as LayoutMode)
  const choose = useCallback((next: LayoutMode) => setLayoutMode(next), [])
  return [mode, choose]
}
